import React from 'react'
import { defineMessages, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { ActivityBadge } from '../../atoms/activityBadge/ActivityBadge'
import { BodyText, Size } from '../../atoms/bodyText/BodyText'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Tooltip, TOOLTIP_TRIGGER_ICON } from '../../atoms/tooltip/Tooltip'
import { BadgeShape } from '../../badge/constants'
import { IS_WEB } from '../../constants'
import {
  CoordinatedCareThreadModel,
  CoordinatedCareThreadSection,
} from '../../ui-models/messages/CoordinatedCareThreads'
import { tID } from '../../utils/utils'

// No left slop: the badge sits right after SelectThread, so extending left would steal thread-selection taps.
const STATUS_BADGE_HIT_SLOP = { top: 24, right: 16, bottom: 24, left: 0 }

const headerMessages = defineMessages({
  client: { defaultMessage: 'CLIENT', description: 'Concurrent-care client thread section' },
  'client-caregiver': { defaultMessage: 'CLIENT & CAREGIVER', description: 'Dependent and caregiver thread section' },
  'with-client': { defaultMessage: 'WITH CLIENT', description: 'Client-facing NP testing thread section' },
  'provider-team-only': { defaultMessage: 'PROVIDER TEAM ONLY', description: 'Internal NP testing thread section' },
  'provider-team': { defaultMessage: 'PROVIDER TEAM', description: 'Concurrent-care provider thread section' },
})

const statusMessages = defineMessages({
  inactive: { defaultMessage: 'INACTIVE', description: 'Provider no longer treating this client' },
  unavailable: { defaultMessage: 'UNAVAILABLE', description: 'Provider currently unable to be reached' },
  deactivated: { defaultMessage: 'DEACTIVATED', description: 'Provider no longer with Lyra' },
})

const statusTooltips = defineMessages({
  inactive: {
    defaultMessage: 'The client has been discharged from this provider’s care.',
    description: 'Inactive provider row badge tooltip',
  },
  unavailable: {
    defaultMessage: 'This provider is unable to be reached right now.',
    description: 'Unavailable provider row badge tooltip',
  },
  deactivated: {
    defaultMessage: 'This provider is no longer with Lyra.',
    description: 'Deactivated provider row badge tooltip',
  },
})

const topologyMessages = defineMessages({
  client: { defaultMessage: 'Client conversation', description: 'Accessible client thread type' },
  'external-group': { defaultMessage: 'Group with client', description: 'Accessible external group thread type' },
  'internal-group': { defaultMessage: 'Provider-only group', description: 'Accessible internal group thread type' },
  'provider-pair': { defaultMessage: 'Provider conversation', description: 'Accessible provider-pair thread type' },
})

const messages = defineMessages({
  providerTeamTooltip: {
    defaultMessage:
      "Any listed providers have treated this client and completed HIPAA training through Lyra. No ROI is needed to coordinate their care. Messages are not part of the client's medical record.",
    description: 'Legal-approved provider team section tooltip',
  },
  providerTeamInfo: {
    defaultMessage: 'About provider team messaging',
    description: 'Provider section information button',
  },
  readOnly: { defaultMessage: 'Read-only', description: 'Thread history can be opened but messages cannot be sent' },
  unread: {
    defaultMessage: '{count, plural, one {# unread message} other {# unread messages}}',
    description: 'Unread messages in a client or caregiver conversation',
  },
  list: { defaultMessage: 'Client conversations', description: 'Per-client conversation list accessible label' },
})

const List = styled.ScrollView(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
}))

const Header = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
  paddingHorizontal: theme.spacing['16px'],
  paddingVertical: theme.spacing['8px'],
  backgroundColor: theme.colors.backgroundSecondary,
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: theme.colors.borderDefault,
}))

const Row = styled.View<{ $selected: boolean }>(({ theme, $selected }) => ({
  position: 'relative',
  flexDirection: 'row',
  alignItems: 'center',
  paddingRight: theme.spacing['16px'],
  borderBottomWidth: 1,
  borderBottomColor: theme.colors.borderDefault,
  backgroundColor: $selected ? theme.colors.backgroundFocus : theme.colors.backgroundPrimary,
}))

const SelectThread = styled(PressableOpacity)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  gap: theme.spacing['2px'],
  paddingHorizontal: theme.spacing['16px'],
  paddingVertical: theme.spacing['16px'],
}))

const ThreadContent = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))
const ThreadLabels = styled.View(({ theme }) => ({ flex: 1, minWidth: 0, gap: theme.spacing['2px'] }))

const SelectedIndicator = styled.View(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 3,
  backgroundColor: theme.colors.borderActive,
}))

const ThreadRow = ({
  thread,
  selected,
  onThreadPress,
}: {
  thread: CoordinatedCareThreadModel
  selected: boolean
  onThreadPress: (id: string) => void
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const title = thread.participantNames.join(', ')
  const status = thread.providerStatus
  const statusLabel = status ? formatMessage(statusMessages[status]) : undefined
  const statusTooltip = status ? formatMessage(statusTooltips[status]) : undefined
  const unreadCount = thread.unreadCount ?? 0
  const unreadLabel = unreadCount > 0 ? formatMessage(messages.unread, { count: unreadCount }) : undefined
  const readOnlyLabel = thread.isReadOnly ? formatMessage(messages.readOnly) : undefined

  return (
    <Row $selected={selected} testID={tID(`CoordinatedCareThread-${thread.id}`)}>
      <SelectThread
        accessibilityLabel={[
          title,
          thread.subtitle,
          formatMessage(topologyMessages[thread.topology]),
          statusLabel,
          readOnlyLabel,
          unreadLabel,
        ]
          .filter(Boolean)
          .join(', ')}
        accessibilityState={{ selected }}
        aria-current={selected ? 'true' : undefined}
        hoveredBackgroundColor={colors.backgroundSecondary}
        pressedBackgroundColor={colors.backgroundFocus}
        onPress={() => onThreadPress(thread.id)}
        testID={tID(`CoordinatedCareThread-select-${thread.id}`)}
      >
        <ThreadContent>
          <ThreadLabels>
            <BodyText text={title} numberOfLines={1} testID={tID(`CoordinatedCareThread-title-${thread.id}`)} />
            <BodyText text={thread.subtitle} size={Size.SMALL} color={colors.textSecondary} numberOfLines={1} />
          </ThreadLabels>
          {unreadCount > 0 && (
            <ActivityBadge text={unreadCount} testID={tID(`CoordinatedCareThread-unread-${thread.id}`)} />
          )}
        </ThreadContent>
      </SelectThread>
      {/* Keep the badge trigger beside the selection button, avoiding nested buttons and accidental selection. */}
      {status && (
        <Tooltip
          content={statusTooltip}
          accessibilityLabel={`${statusLabel}: ${statusTooltip}`}
          placement='top'
          hoverEnabled={IS_WEB}
          openOnPress
          closeOnScroll
          hitSlop={STATUS_BADGE_HIT_SLOP}
          wrapperStyle={{ flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }}
          testID={`CoordinatedCareThread-status-${thread.id}`}
        >
          <ActivityBadge text={statusLabel} shape={BadgeShape.RECT} bold />
        </Tooltip>
      )}
      {selected && (
        <SelectedIndicator pointerEvents='none' testID={tID(`CoordinatedCareThread-selectedIndicator-${thread.id}`)} />
      )}
    </Row>
  )
}

export type CoordinatedCareThreadListProps = {
  /** One section per header, in the display order supplied by the caller. */
  sections: CoordinatedCareThreadSection[]
  selectedId: string | null
  onThreadPress: (id: string) => void
}

/**
 * Full-page name/program sidebar from the coordinated-care designs.
 * ProviderChatThreadList retains the preview/date/unread presentation used by message popovers.
 * Grouping and access decisions come from the caller.
 */
export const CoordinatedCareThreadList = React.memo(function CoordinatedCareThreadList({
  sections,
  selectedId,
  onThreadPress,
}: CoordinatedCareThreadListProps) {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  return (
    <List testID={tID('CoordinatedCareThreadList')} accessibilityLabel={formatMessage(messages.list)}>
      {sections
        .filter(({ threads }) => threads.length > 0)
        .map(({ header, threads }) => (
          <React.Fragment key={header}>
            <Header>
              <BodyText
                text={formatMessage(headerMessages[header])}
                size={Size.CAPTION}
                bold
                accessibilityRole='header'
              />
              {(header === 'provider-team' || header === 'provider-team-only') && (
                <Tooltip
                  content={formatMessage(messages.providerTeamTooltip)}
                  accessibilityLabel={formatMessage(messages.providerTeamInfo)}
                  triggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO, size: 14, fillColor: colors.textSecondary }}
                  hoverEnabled={IS_WEB}
                  openOnPress
                  closeOnScroll
                  wrapperStyle={{ flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }}
                  testID={`CoordinatedCareThreadList-info-${header}`}
                />
              )}
            </Header>
            {threads.map((thread) => (
              <ThreadRow
                key={thread.id}
                thread={thread}
                selected={thread.id === selectedId}
                onThreadPress={onThreadPress}
              />
            ))}
          </React.Fragment>
        ))}
    </List>
  )
})
